import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import Layout from '../components/Layout';
import { FaEdit, FaTrash, FaSearch, FaTimes } from 'react-icons/fa';
import { formatRupiah } from '../utils/formatter';

const DataBuku = () => {
    const [buku, setBuku] = useState([]);
    const [kategori, setKategori] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [hargaInput, setHargaInput] = useState('');
    const [search, setSearch] = useState('');
    const [filteredData, setFilteredData] = useState([]);
    const [entriesToShow, setEntriesToShow] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    
    // State untuk multiple kategori
    const [selectedKategori, setSelectedKategori] = useState([]);
    
    const [formData, setFormData] = useState({
        judul: '',
        pengarang: '',
        tahun_terbit: '',
        cetakan: '',
        edisi: '',
        copyright: '',
        penerbit: '',
        asal_buku: '',
        harga: '',
        rak_buku: '',
        jumlah_buku: '',
        keterangan: '',
        foto_buku: null,
        foto_buku_existing: '',
        no_induk_buku: '',
    });

    useEffect(() => {
        fetchBuku();
        fetchKategori();
        fetchNextNoIndukBuku();
    }, []);

    const fetchBuku = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/buku`);
            setBuku(response.data);
            setFilteredData(response.data);
            setLoading(false);
        } catch (error) {
            Swal.fire('Gagal', 'Gagal memuat data buku. Silakan coba lagi.', 'error');
            setLoading(false);
        }
    };

    const fetchKategori = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/kategori`);
            setKategori(response.data);
        } catch (error) {
            Swal.fire('Gagal', 'Gagal memuat data kategori.', 'error');
        }
    };

    const fetchNextNoIndukBuku = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/buku/next-no-induk`);
            setFormData(prev => ({
                ...prev,
                no_induk_buku: response.data.nextNoInduk
            }));
        } catch (error) {
            console.error('Error fetching next no_induk_buku:', error);
        }
    };

    useEffect(() => {
        const result = buku.filter(item =>
            item.judul.toLowerCase().includes(search.toLowerCase()) ||
            item.no_induk_buku.toLowerCase().includes(search.toLowerCase()) ||
            (item.pengarang && item.pengarang.toLowerCase().includes(search.toLowerCase())) ||
            (item.nama_kategori && item.nama_kategori.toLowerCase().includes(search.toLowerCase()))
        );
        setFilteredData(result);
        setCurrentPage(1);
    }, [search, buku]);

    // ✅ FIXED: Function untuk mendapatkan kategori unik tanpa duplikat
    const getUniqueKategoriArray = (namaKategori) => {
        if (!namaKategori) return [];
        
        // Split by comma, trim, dan hapus duplikat
        const kategoris = namaKategori
            .split(',')
            .map(k => k.trim())
            .filter(k => k);
        
        // Hapus duplikat menggunakan Set
        return [...new Set(kategoris)];
    };

    // Logika Pagination
    const lastEntryIndex = currentPage * entriesToShow;
    const firstEntryIndex = lastEntryIndex - entriesToShow;
    const currentEntries = filteredData.slice(firstEntryIndex, lastEntryIndex);
    const totalPages = Math.ceil(filteredData.length / entriesToShow);

    const handleEntriesChange = (e) => {
        setEntriesToShow(Number(e.target.value));
        setCurrentPage(1);
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const renderPagination = () => {
        const pages = [];
        for (let i = 1; i <= totalPages; i++) {
            pages.push(
                <li key={i}>
                    <button
                        className={`pagination-link ${i === currentPage ? 'is-current' : ''}`}
                        onClick={() => handlePageChange(i)}
                    >
                        {i}
                    </button>
                </li>
            );
        }
        return pages;
    };

    const handleSearch = (e) => {
        setSearch(e.target.value);
    };

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        if (name === 'foto_buku') {
            setFormData(prev => ({ ...prev, [name]: files[0] }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleHargaChange = (e) => {
        let value = e.target.value;
        setHargaInput(value);
        const rawValue = value.replace(/[Rp.,]/g, '');
        setFormData(prev => ({ ...prev, harga: rawValue }));
    };

    // Handle multiple kategori selection
    const handleKategoriSelect = (kategoriId) => {
        if (!selectedKategori.includes(kategoriId)) {
            setSelectedKategori([...selectedKategori, kategoriId]);
        }
    };

    const handleKategoriRemove = (kategoriId) => {
        setSelectedKategori(selectedKategori.filter(id => id !== kategoriId));
    };

    const getKategoriName = (kategoriId) => {
        const kat = kategori.find(k => k.id === kategoriId);
        return kat ? kat.nama_kategori : '';
    };

    const renderKategoriTags = () => {
        return selectedKategori.map(kategoriId => (
            <span key={kategoriId} className="tag is-info mr-2 mb-2">
                {getKategoriName(kategoriId)}
                <button 
                    type="button"
                    className="delete is-small ml-1" 
                    onClick={() => handleKategoriRemove(kategoriId)}
                >
                    <FaTimes style={{ fontSize: '8px' }} />
                </button>
            </span>
        ));
    };

    const openModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditId(null);
        setHargaInput('');
        setSelectedKategori([]);
        setFormData({
            judul: '',
            pengarang: '',
            tahun_terbit: '',
            cetakan: '',
            edisi: '',
            copyright: '',
            penerbit: '',
            asal_buku: '',
            harga: '',
            rak_buku: '',
            jumlah_buku: '',
            keterangan: '',
            foto_buku: null,
            foto_buku_existing: '',
            no_induk_buku: '',
        });
        fetchNextNoIndukBuku();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (selectedKategori.length === 0) {
            Swal.fire('Peringatan', 'Pilih minimal satu kategori untuk buku ini.', 'warning');
            return;
        }

        const data = new FormData();
        for (const key in formData) {
            if (formData[key] !== null) {
                data.append(key, formData[key]);
            }
        }
        
        // Tambahkan kategori sebagai JSON string
        data.append('kategori_ids', JSON.stringify(selectedKategori));

        try {
            if (editId) {
                await axios.put(`${process.env.REACT_APP_API_URL}/buku/${editId}`, data, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
                Swal.fire('Berhasil', 'Data buku berhasil diperbarui', 'success');
            } else {
                await axios.post(`${process.env.REACT_APP_API_URL}/buku`, data, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
                Swal.fire('Berhasil', 'Buku baru berhasil ditambahkan', 'success');
            }
            closeModal();
            fetchBuku();
        } catch (error) {
            console.error('Error submitting form:', error);
            Swal.fire('Gagal', error.response?.data?.message || 'Terjadi kesalahan saat menyimpan data. Periksa kembali input Anda.', 'error');
        }
    };

    const handleEdit = async (id) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/buku/${id}`);
            const data = response.data;
            setEditId(id);
            setHargaInput(formatRupiah(data.harga));
            setSelectedKategori(data.kategori_list || []);
            setFormData({
                judul: data.judul,
                pengarang: data.pengarang || '',
                tahun_terbit: data.tahun_terbit || '',
                cetakan: data.cetakan || '',
                edisi: data.edisi || '',
                copyright: data.copyright || '',
                penerbit: data.penerbit || '',
                asal_buku: data.asal_buku,
                harga: data.harga,
                rak_buku: data.rak_buku || '',
                jumlah_buku: data.jumlah_buku,
                keterangan: data.keterangan || '',
                foto_buku: null,
                foto_buku_existing: data.foto_buku,
                no_induk_buku: data.no_induk_buku || '',
            });
            openModal();
        } catch (error) {
            Swal.fire('Gagal', 'Gagal memuat data buku untuk diedit.', 'error');
        }
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: 'Apakah Anda yakin?',
            text: "Anda tidak akan bisa mengembalikan ini!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Ya, hapus!',
            cancelButtonText: 'Batal'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await axios.delete(`${process.env.REACT_APP_API_URL}/buku/${id}`);
                    Swal.fire(
                        'Dihapus!',
                        'Data buku berhasil dihapus.',
                        'success'
                    );
                    fetchBuku();
                } catch (error) {
                    Swal.fire('Gagal', 'Gagal menghapus data buku.', 'error');
                }
            }
        });
    };

    return (
        <Layout title="Data Buku">
            <div className="box">
                {/* Bagian filter dan tombol */}
                <div className="columns is-vcentered">
                    {/* Select untuk show entries */}
                    <div className="column is-narrow">
                        <div className="field is-horizontal">
                            <div className="field-body">
                                <div className="field is-narrow">
                                    <div className="control">
                                        <div className="select">
                                            <select value={entriesToShow} onChange={handleEntriesChange}>
                                                <option value="10">10</option>
                                                <option value="25">25</option>
                                                <option value="50">50</option>
                                                <option value="100">100</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Search bar */}
                    <div className="column">
                        <div className="field">
                            <div className="control has-icons-left">
                                <input
                                    className="input"
                                    type="text"
                                    placeholder="Cari buku..."
                                    value={search}
                                    onChange={handleSearch}
                                />
                                <span className="icon is-small is-left">
                                    <FaSearch />
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Tombol Tambah Buku */}
                    <div className="column is-narrow">
                        <div className="buttons is-right">
                            <button className="button is-info" onClick={openModal}>
                                Tambah Buku
                            </button>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <p className="has-text-centered">Memuat data...</p>
                ) : (
                    <div className="table-container">
                        <table className="table is-striped is-fullwidth" style={{ tableLayout: 'fixed' }}>
                            <thead>
                                <tr>
                                    <th style={{ width: '50px' }}>No.</th>
                                    <th style={{ width: '80px' }}>Foto</th>
                                    <th style={{ width: '100px' }}>No. Induk</th>
                                    <th style={{ width: '200px' }}>Judul</th>
                                    <th style={{ width: '150px' }}>Pengarang</th>
                                    <th style={{ width: '150px' }}>Penerbit</th>
                                    <th style={{ width: '180px' }}>Kategori</th>
                                    <th style={{ width: '100px' }}>Stok Tersedia</th>
                                    <th style={{ width: '120px' }}>Aksi</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentEntries.length > 0 ? (
                                    currentEntries.map((item, index) => {
                                        // ✅ FIXED: Dapatkan kategori unik untuk setiap buku
                                        const uniqueKategoris = getUniqueKategoriArray(item.nama_kategori);
                                        
                                        return (
                                            <tr key={item.id} style={{ height: '80px', verticalAlign: 'middle' }}>
                                                <td style={{ verticalAlign: 'middle' }}>
                                                    {firstEntryIndex + index + 1}
                                                </td>
                                                <td style={{ verticalAlign: 'middle', padding: '10px' }}>
                                                    {item.foto_buku ? (
                                                        <div style={{
                                                            width: '70px',
                                                            height: '100px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            overflow: 'hidden',
                                                            borderRadius: '4px',
                                                            backgroundColor: '#f5f5f5'
                                                        }}>
                                                            <img
                                                                src={`${process.env.REACT_APP_BASE_URL}/images/buku/${item.foto_buku}`}
                                                                alt={item.judul}
                                                                style={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'cover'
                                                                }}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div style={{
                                                            width: '60px',
                                                            height: '60px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            borderRadius: '4px',
                                                            backgroundColor: '#f5f5f5',
                                                            color: '#ccc',
                                                            fontSize: '10px'
                                                        }}>
                                                            No Image
                                                        </div>
                                                    )}
                                                </td>
                                                <td style={{ verticalAlign: 'middle' }}>{item.no_induk_buku}</td>
                                                <td style={{ verticalAlign: 'middle' }} title={item.judul}>{item.judul}</td>
                                                <td style={{ verticalAlign: 'middle' }} title={item.pengarang}>{item.pengarang}</td>
                                                <td style={{ verticalAlign: 'middle' }} title={item.penerbit}>{item.penerbit}</td>
                                                <td style={{ verticalAlign: 'middle' }}>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                        {/* ✅ FIXED: Render kategori unik saja */}
                                                        {uniqueKategoris.length > 0 ? (
                                                            uniqueKategoris.map((kat, idx) => (
                                                                <span key={idx} className="tag is-small is-light">
                                                                    {kat}
                                                                </span>
                                                            ))
                                                        ) : (
                                                            <span className="tag is-small is-light">
                                                                Tidak ada kategori
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td style={{ verticalAlign: 'middle', textAlign: 'center' }}>{item.stok_tersedia}</td>
                                                <td style={{ verticalAlign: 'middle' }}>
                                                    <div className="buttons are-small">
                                                        <button className="button is-warning" onClick={() => handleEdit(item.id)}>
                                                            <FaEdit />
                                                        </button>
                                                        <button className="button is-danger" onClick={() => handleDelete(item.id)}>
                                                            <FaTrash />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="9" className="has-text-centered" style={{ padding: '40px' }}>
                                            Tidak ada data buku.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
                <nav className="pagination is-centered" role="navigation" aria-label="pagination">
                    <button className="pagination-previous" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                        Previous
                    </button>
                    <button className="pagination-next" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
                        Next
                    </button>
                    <ul className="pagination-list">
                        {renderPagination()}
                    </ul>
                </nav>
            </div>

            {/* Modal untuk Tambah/Edit */}
            <div className={`modal ${isModalOpen ? 'is-active' : ''}`}>
                <div className="modal-background" onClick={closeModal}></div>
                <div className="modal-card">
                    <header className="modal-card-head">
                        <p className="modal-card-title">{editId ? 'Edit Buku' : 'Tambah Buku'}</p>
                        <button className="delete" aria-label="close" onClick={closeModal}></button>
                    </header>
                    <section className="modal-card-body">
                        <form onSubmit={handleSubmit} encType="multipart/form-data">
                            <div className="field">
                                <label className="label">No. Induk Buku</label>
                                <div className="control">
                                    <input className="input" type="text" name="no_induk_buku" value={formData.no_induk_buku} onChange={handleChange} readOnly />
                                </div>
                            </div>
                            <div className="field">
                                <label className="label">Judul Buku</label>
                                <div className="control">
                                    <input className="input" type="text" name="judul" placeholder="Judul Buku" value={formData.judul} onChange={handleChange} required />
                                </div>
                            </div>
                            <div className="field">
                                <label className="label">Pengarang</label>
                                <div className="control">
                                    <input className="input" type="text" name="pengarang" placeholder="Nama Pengarang" value={formData.pengarang} onChange={handleChange} />
                                </div>
                            </div>
                            <div className="field">
                                <label className="label">Tahun Terbit</label>
                                <div className="control">
                                    <input className="input" type="number" name="tahun_terbit" placeholder="Tahun Terbit" value={formData.tahun_terbit} onChange={handleChange} />
                                </div>
                            </div>
                            <div className="field">
                                <label className="label">Penerbit</label>
                                <div className="control">
                                    <input className="input" type="text" name="penerbit" placeholder="Nama Penerbit" value={formData.penerbit} onChange={handleChange} />
                                </div>
                            </div>
                            <div className="field">
                                <label className="label">Asal Buku</label>
                                <div className="control">
                                    <input className="input" type="text" name="asal_buku" placeholder="Asal Buku" value={formData.asal_buku} onChange={handleChange} required />
                                </div>
                            </div>
                            <div className="field">
                                <label className="label">Harga</label>
                                <div className="control">
                                    <input className="input" type="text" name="harga" placeholder="Harga" value={hargaInput} onChange={handleHargaChange} />
                                </div>
                            </div>
                            
                            {/* Form Kategori Multiple Selection */}
                            <div className="field">
                                <label className="label">Kategori *</label>
                                <div className="control">
                                    <div className="select is-fullwidth">
                                        <select 
                                            value="" 
                                            onChange={(e) => {
                                                if (e.target.value) {
                                                    handleKategoriSelect(parseInt(e.target.value));
                                                    e.target.value = ""; // Reset select
                                                }
                                            }}
                                        >
                                            <option value="">Pilih Kategori untuk Ditambahkan</option>
                                            {kategori
                                                .filter(cat => !selectedKategori.includes(cat.id))
                                                .map(cat => (
                                                    <option key={cat.id} value={cat.id}>
                                                        {cat.nama_kategori}
                                                    </option>
                                                ))
                                            }
                                        </select>
                                    </div>
                                </div>
                                {/* Display selected categories as tags */}
                                <div className="mt-3">
                                    {selectedKategori.length > 0 && (
                                        <div className="field">
                                            <label className="label is-small">Kategori Terpilih:</label>
                                            <div className="tags">
                                                {renderKategoriTags()}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <p className="help">Pilih minimal satu kategori. Klik 'X' pada tag untuk menghapus kategori.</p>
                            </div>

                            <div className="field">
                                <label className="label">Rak Buku</label>
                                <div className="control">
                                    <input className="input" type="text" name="rak_buku" placeholder="Lokasi Rak Buku" value={formData.rak_buku} onChange={handleChange} />
                                </div>
                            </div>
                            <div className="field">
                                <label className="label">Jumlah Buku</label>
                                <div className="control">
                                    <input className="input" type="number" name="jumlah_buku" placeholder="Jumlah Buku" value={formData.jumlah_buku} onChange={handleChange} required />
                                </div>
                            </div>
                            <div className="field">
                                <label className="label">Cetakan</label>
                                <div className="control">
                                    <input className="input" type="text" name="cetakan" placeholder="Cetakan" value={formData.cetakan} onChange={handleChange} />
                                </div>
                            </div>
                            <div className="field">
                                <label className="label">Edisi</label>
                                <div className="control">
                                    <input className="input" type="text" name="edisi" placeholder="Edisi" value={formData.edisi} onChange={handleChange} />
                                </div>
                            </div>
                            <div className="field">
                                <label className="label">Copyright</label>
                                <div className="control">
                                    <input className="input" type="text" name="copyright" placeholder="Copyright" value={formData.copyright} onChange={handleChange} />
                                </div>
                            </div>
                            <div className="field">
                                <label className="label">Foto Buku</label>
                                <div className="control">
                                    <input className="input" type="file" name="foto_buku" onChange={handleChange} />
                                </div>
                            </div>
                            {editId && formData.foto_buku_existing && (
                                <div className="field">
                                    <label className="label">Foto Saat Ini</label>
                                    <figure className="image is-128x128">
                                        <img src={`${process.env.REACT_APP_BASE_URL}/images/buku/${formData.foto_buku_existing}`} alt="Foto buku" />
                                    </figure>
                                </div>
                            )}

                            <div className="field">
                                <label className="label">Keterangan</label>
                                <div className="control">
                                    <textarea className="textarea" name="keterangan" placeholder="Catatan tambahan" value={formData.keterangan} onChange={handleChange}></textarea>
                                </div>
                            </div>
                            <div className="field mt-5">
                                <div className="control">
                                    <button className="button is-info is-fullwidth" type="submit">
                                        Simpan
                                    </button>
                                </div>
                            </div>
                        </form>
                    </section>
                </div>
            </div>
        </Layout>
    );
};

export default DataBuku;